import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Download, Trash2, Mail } from "lucide-react";
import { toast } from "sonner";
import { adminListSubscribers, adminDeleteSubscriber, adminSubscriberExportUrl, getToken } from "@/lib/api";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function AdminSubscribers() {
  const [data, setData] = useState({ subscribers: [], active: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [filter, setFilter] = useState("active"); // active | unsubscribed | all

  const load = async () => {
    setLoading(true);
    try {
      setData(await adminListSubscribers());
    } catch {
      toast.error("Couldn't load subscribers.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onDelete = async () => {
    if (!deleteTarget) return;
    try {
      await adminDeleteSubscriber(deleteTarget.email);
      toast.success(`Removed ${deleteTarget.email}.`);
      setDeleteTarget(null);
      load();
    } catch {
      toast.error("Remove failed.");
    }
  };

  const onExport = async () => {
    try {
      const res = await fetch(adminSubscriberExportUrl(), {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error("export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "subscribers.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed.");
    }
  };

  const filtered = data.subscribers.filter((s) => {
    if (filter === "active") return !s.unsubscribed;
    if (filter === "unsubscribed") return s.unsubscribed;
    return true;
  });

  return (
    <div data-testid="admin-subscribers-page">
      <Link
        to="/admin/newsletter"
        className="inline-flex items-center gap-1.5 text-sm text-ink-muted hover:text-forest link-underline mb-6"
      >
        <ArrowLeft className="w-4 h-4" /> Back to newsletters
      </Link>

      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <p className="overline text-terracotta">Subscribers</p>
          <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight text-ink mt-2">
            Your mailing list
          </h1>
          <p className="text-sm text-ink-muted mt-2">
            {data.total} total · <strong className="text-forest">{data.active} active</strong> · {data.total - data.active} unsubscribed
          </p>
        </div>
        <button
          onClick={onExport}
          data-testid="subscribers-export"
          className="inline-flex items-center gap-2 bg-paper-100 border border-paper-200 text-ink px-4 py-2.5 text-sm font-semibold rounded-sm hover:bg-paper-200 transition-colors"
        >
          <Download className="w-4 h-4" /> Export CSV
        </button>
      </div>

      <div className="flex gap-2 mb-5">
        {[
          { k: "active", label: "Active" },
          { k: "unsubscribed", label: "Unsubscribed" },
          { k: "all", label: "All" },
        ].map((f) => (
          <button
            key={f.k}
            onClick={() => setFilter(f.k)}
            data-testid={`filter-${f.k}`}
            className={`px-3 py-1.5 text-xs font-semibold rounded-sm transition-colors ${
              filter === f.k
                ? "bg-forest text-paper-50"
                : "bg-paper-100 border border-paper-200 text-ink-muted hover:text-ink"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading && <div className="text-ink-muted text-sm">Loading…</div>}

      {!loading && filtered.length === 0 && (
        <div className="bg-paper-100 border border-paper-200 p-10 rounded-sm text-center" data-testid="subscribers-empty">
          <Mail className="w-8 h-8 text-ink-muted mx-auto mb-3" />
          <p className="text-ink-muted">No {filter === "all" ? "" : filter + " "}subscribers yet.</p>
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div className="bg-paper-100 border border-paper-200 rounded-sm overflow-hidden" data-testid="subscribers-list">
          <table className="w-full text-sm">
            <thead className="bg-paper-200 text-ink">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">Email</th>
                <th className="text-left px-5 py-3 font-semibold">Subscribed</th>
                <th className="text-left px-5 py-3 font-semibold">Status</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => (
                <tr key={s.email} data-testid={`subscriber-row-${s.email}`} className="border-t border-paper-200">
                  <td className="px-5 py-3 text-ink font-medium">{s.email}</td>
                  <td className="px-5 py-3 text-ink-muted">{s.subscribed_at?.slice(0, 10)}</td>
                  <td className="px-5 py-3">
                    {s.unsubscribed ? (
                      <span className="inline-block text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm bg-paper-200 text-ink">
                        Unsubscribed
                      </span>
                    ) : (
                      <span className="inline-block text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm bg-forest text-paper-50">
                        Active
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <button
                      onClick={() => setDeleteTarget(s)}
                      data-testid={`subscriber-delete-${s.email}`}
                      className="inline-flex items-center gap-1 text-xs text-ink-muted hover:text-terracotta"
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="subscriber-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this subscriber?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.email} will be permanently removed from your mailing list.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="subscriber-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={onDelete}
              data-testid="subscriber-delete-confirm"
              className="bg-terracotta hover:bg-terracotta-dark text-paper-50"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
